import {useRuntimeEnvironment} from '@github-ui/ssr-utils/use-runtime-environment'
import type {NativeSelectionMap} from '@github-ui/taillights-symbols/native-selection'
import type {SourceRange, SourceSymbol} from '@github-ui/taillights-worker/document'
import {testIdProps} from '@github-ui/test-id-props'
import {CommandEvent, ScopedCommands} from '@github-ui/ui-commands'
import {EllipsisIcon} from '@primer/octicons-react'
import {Banner, Button} from '@primer/react'
import {clsx} from 'clsx'
import {
  type ComponentProps,
  type KeyboardEvent,
  type Ref,
  use,
  useCallback,
  useLayoutEffect,
  useMemo,
  useRef,
} from 'react'

import {useIsFirefox} from '../../utilities/lines'
import {ExperimentalSourceOptionsContext} from './experimental-source-options'
import {SourceLabelContext, useCodeBlobStartupError} from './source-context'
import {SourceModelContext, SourceModelProvider, useSourceModelContext} from './source-model'
import {lineRange} from './source-navigation'
import {TaillightsPreviewControlsContext} from './taillights-preview-controls'
import styles from './TaillightsCodeView.module.css'
import type {TaillightsGutterAction} from './TaillightsGutterRow'
import type {GutterActions} from './use-gutter-selection'
import {SourceChunks, WindowedSource} from './WindowedSource'

export interface CodeBlobTaillightsProps {
  embedded?: boolean
  foldingAvailable?: boolean
  showFoldButtons?: boolean
  /** Errors keyed by original, 1-based source line. */
  codeownersErrors?: Readonly<Record<number, string>>
  onStickyLineRemoval?: (originalLineNumber: number) => void
  ref?: Ref<HTMLTextAreaElement>
  highlightRanges?: readonly SourceRange[]
  focusedHighlightRange?: SourceRange | null
  onSymbolSelected?: (symbol: SourceSymbol) => void
  gutterActions?: GutterActions
  commands?: ComponentProps<typeof ScopedCommands>['commands']
  onLineMount?: (line: number, element: HTMLAnchorElement) => void | (() => void)
}

const noActions: readonly TaillightsGutterAction[] = []
const ssrTextareaLineLimit = 1000

/** Unwrapped source viewer. Its CodeBlobTaillightsProvider must be owned by the caller. */
export function CodeBlobTaillights({foldingAvailable, onStickyLineRemoval, ...props}: CodeBlobTaillightsProps) {
  const sharedModel = use(SourceModelContext)
  if (sharedModel) return <SourceContent {...props} />
  return (
    <SourceModelProvider foldingAvailable={foldingAvailable} onStickyLineRemoval={onStickyLineRemoval}>
      <SourceContent {...props} />
    </SourceModelProvider>
  )
}

function SourceContent({
  embedded = false,
  ref,
  codeownersErrors,
  highlightRanges: controlledRanges,
  focusedHighlightRange,
  onSymbolSelected: reportSymbol,
  gutterActions,
  commands,
  onLineMount,
  showFoldButtons = true,
}: Omit<CodeBlobTaillightsProps, 'foldingAvailable' | 'onStickyLineRemoval'>) {
  const model = useSourceModelContext()
  const {
    navigation,
    materializeAllLines,
    virtualized,
    reverseSticky,
    linesToBuffer,
    linesPerChunk,
    scrollSurfaceRef,
    stickyOffsetRef,
    stickyWindowRef,
    sourcePlaneRef,
    reverseStickyRefs,
    fallbackText,
    renderError,
    onRenderError,
    lineHighlight,
    highlightRanges: localRanges,
    focusedResult: localFocusedResult,
    activeRange,
    highlightLines,
    gutterSelection,
    onSymbolSelected: inspectLocalSymbol,
  } = model
  const {
    lines,
    sections,
    foldingEnabled,
    foldingReady,
    symbolsRef,
    textareaRef,
    viewportRef,
    documentRef,
    scrollMargin,
    collapsed,
    foldMarkers,
    visible,
    view,
    textProjection,
    lineHeight,
    error,
    onSelectionChange,
    anchorRange,
    toggleFold,
    expandFold,
  } = navigation
  const {isBrowser} = useRuntimeEnvironment()
  const nativeProjection = useMemo<NativeSelectionMap>(() => {
    if (isBrowser || textProjection.lines.length <= ssrTextareaLineLimit) return textProjection
    const nativeLines = textProjection.lines.slice(0, ssrTextareaLineLimit)
    const lineNumbers = textProjection.lineNumbers.slice(0, ssrTextareaLineLimit)
    return {
      lines: nativeLines,
      text: nativeLines.join('\n'),
      lineNumbers,
      lineStarts: textProjection.lineStarts.slice(0, ssrTextareaLineLimit),
      sourceToDisplay: new Map(lineNumbers.map((line, index) => [line, index])),
    }
  }, [isBrowser, textProjection])
  const hydrationSelection = useRef<Pick<
    HTMLTextAreaElement,
    'selectionStart' | 'selectionEnd' | 'selectionDirection'
  > | null>(null)
  useLayoutEffect(() => {
    const textarea = textareaRef.current
    if (!textarea) return
    if (nativeProjection !== textProjection) {
      hydrationSelection.current = {
        selectionStart: textarea.selectionStart,
        selectionEnd: textarea.selectionEnd,
        selectionDirection: textarea.selectionDirection,
      }
    } else if (hydrationSelection.current) {
      // Extending a controlled textarea value moves its selection to the end.
      const {selectionStart, selectionEnd, selectionDirection} = hydrationSelection.current
      hydrationSelection.current = null
      textarea.setSelectionRange(selectionStart, selectionEnd, selectionDirection)
    }
  }, [nativeProjection, textProjection, textareaRef])
  const highlightRanges = useMemo(
    () =>
      controlledRanges === undefined
        ? localRanges
        : anchorRange && !lineHighlight
          ? [...controlledRanges, anchorRange]
          : controlledRanges,
    [controlledRanges, localRanges, anchorRange, lineHighlight],
  )
  const focusedResult = focusedHighlightRange === undefined ? localFocusedResult : focusedHighlightRange
  const onSymbolSelected = reportSymbol ?? inspectLocalSymbol
  const options = use(ExperimentalSourceOptionsContext)
  const isFirefox = useIsFirefox()
  const controls = use(TaillightsPreviewControlsContext)
  const startupError = useCodeBlobStartupError()
  const sourceLabel = use(SourceLabelContext)
  const nativeRef = useCallback(
    (node: HTMLTextAreaElement) => {
      textareaRef.current = node
      const cleanup = typeof ref === 'function' ? ref(node) : undefined
      if (ref && typeof ref !== 'function') ref.current = node
      return () => {
        textareaRef.current = null
        if (typeof cleanup === 'function') cleanup()
        else if (typeof ref === 'function') ref(null)
        else if (ref) ref.current = null
      }
    },
    [textareaRef, ref],
  )
  const showLineActions = gutterActions !== undefined || (controls?.showLineActions ?? false)
  const actions = useMemo(
    () => gutterActions ?? controls?.gutterActions ?? {...gutterSelection, actions: noActions},
    [gutterActions, controls?.gutterActions, gutterSelection],
  )
  const foldStarts = useMemo(
    () => new Set(foldingEnabled && showFoldButtons ? sections.map(section => section.startLine) : []),
    [foldingEnabled, showFoldButtons, sections],
  )
  const gutter = useMemo(
    () => ({
      visible,
      foldStarts,
      foldingReady,
      collapsed,
      anchorRange,
      onToggle: toggleFold,
      actions,
      lines,
      codeownersErrors,
      showLineActions,
      onLineMount,
    }),
    [
      visible,
      foldStarts,
      foldingReady,
      collapsed,
      anchorRange,
      toggleFold,
      actions,
      lines,
      codeownersErrors,
      showLineActions,
      onLineMount,
    ],
  )
  const openMenu = {...controls?.commands, ...commands}['code-view:open-line-menu']
  const onSourceKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (event.defaultPrevented || !openMenu || !actions.actions.length || !visible.length) return
      // eslint-disable-next-line @github-ui/ui-commands/no-manual-shortcut-logic -- Native menu keys supplement the existing open-line-menu command; caret/page keys remain native.
      if (event.key === 'ContextMenu' || (event.key === 'F10' && event.shiftKey)) {
        openMenu(new CommandEvent('code-view:open-line-menu'))
        event.preventDefault()
      }
    },
    [openMenu, actions.actions.length, visible.length],
  )
  const sourceHeight = Math.max(lineHeight, view?.height ?? lines.length * lineHeight)
  return (
    <>
      {renderError || error || startupError ? (
        <Banner variant="critical">
          <Banner.Title>Syntax rendering failed</Banner.Title>
          <Banner.Description>{renderError || error || startupError} Native text remains available.</Banner.Description>
        </Banner>
      ) : null}
      <div
        {...testIdProps('taillights-source-container')}
        className={clsx(styles.SourceContainer, embedded && styles.embedded)}
      >
        <div
          {...testIdProps('taillights-scroll-surface')}
          className={clsx(styles.ScrollSurface, embedded && styles.embedded)}
          ref={scrollSurfaceRef}
          data-reverse-sticky={reverseSticky || undefined}
          style={{
            height: reverseSticky
              ? embedded
                ? sourceHeight
                : `calc(${sourceHeight}px + var(--borderWidth-thin) * 2)`
              : undefined,
          }}
        >
          <div className={styles.StickyOffset} ref={stickyOffsetRef} aria-hidden />
          <div className={styles.StickyWindow} ref={stickyWindowRef}>
            <div
              {...testIdProps('taillights-viewport')}
              className={clsx(styles.Viewport, embedded && styles.embedded)}
              ref={viewportRef}
            >
              <div
                className={clsx(styles.Document, !view && styles.unprepared)}
                ref={documentRef}
                data-line-actions={showLineActions}
                data-blob-layout={embedded || undefined}
                style={{
                  width:
                    reverseSticky && view
                      ? `calc(var(--gutter-width) + ${view.width}px + 2 * var(--source-column-padding))`
                      : undefined,
                  height: !view && !reverseSticky ? sourceHeight : undefined,
                }}
              >
                <SourceChunks
                  key={`${linesToBuffer ?? 'chunks'}:${virtualized}:${reverseSticky}`}
                  view={view}
                  projection={textProjection}
                  linesPerChunk={linesPerChunk}
                  renderer={isFirefox ? 'css' : (options.renderer ?? 'bitmap')}
                  safariCSSHighlighting={options.safariCSSHighlighting ?? false}
                  linesToBuffer={linesToBuffer}
                  preparedFor={navigation.preparedFor}
                  virtualized={virtualized}
                  reverseStickyRefs={reverseSticky ? reverseStickyRefs : undefined}
                  gutter={gutter}
                  scrollMargin={scrollMargin}
                  onRenderError={onRenderError}
                  plaintext={materializeAllLines}
                />
                <ScopedCommands
                  as="div"
                  className={clsx(styles.Source, reverseSticky && styles.windowLocalSource)}
                  commands={{
                    ...controls?.commands,
                    ...commands,
                    'code-view:inspect-symbol': () => symbolsRef.current?.inspectSelection(),
                    'code-view:highlight-line': () => highlightLines(lineRange(lines, activeRange().start.line)),
                  }}
                >
                  <WindowedSource
                    preparedFor={navigation.preparedFor}
                    sourceLineCount={lines.length}
                    sourcePlaneRef={sourcePlaneRef}
                    ref={symbolsRef}
                    textareaRef={nativeRef}
                    aria-label={sourceLabel}
                    view={view}
                    initialProjection={nativeProjection}
                    hasPlaintextPaint
                    fallbackText={fallbackText}
                    onSelectionChange={onSelectionChange}
                    onRenderError={onRenderError}
                    onSymbolSelected={onSymbolSelected}
                    highlightRanges={highlightRanges}
                    focusedHighlightRange={focusedResult}
                    onKeyDown={showLineActions && view ? onSourceKeyDown : undefined}
                  >
                    {lineHighlight && (
                      <div
                        {...testIdProps('taillights-line-highlight')}
                        aria-hidden
                        className={styles.LineHighlight}
                        style={lineHighlight}
                      />
                    )}
                    {foldMarkers.length > 0 && (
                      <div className={styles.FoldMarkers}>
                        {foldMarkers.map(({lineNumber, left, top, height}) => (
                          <Button
                            key={lineNumber}
                            className={styles.FoldMarker}
                            variant="invisible"
                            size="small"
                            aria-label={`Expand row ${lineNumber}`}
                            aria-expanded={false}
                            style={{left, top, height}}
                            onMouseDown={event => event.preventDefault()}
                            onClick={event => expandFold(lineNumber, event.detail === 0)}
                          >
                            <EllipsisIcon />
                          </Button>
                        ))}
                      </div>
                    )}
                  </WindowedSource>
                </ScopedCommands>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
